import {
    getAuth,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    type AuthError,
    type User,
} from 'firebase/auth';
import {
    setDoc,
    doc,
    getDoc,
    Timestamp,
    DocumentData,
} from 'firebase/firestore'
import { getFirestoreInstance } from '@/lib/firestore/firebase-singleton';
import { DEFAULT_USER_CRAWL_LIMIT } from '@/lib/firestore/users';
import { sendGAEvent } from '@next/third-parties/google'
import logger from '@/components/logger'

type AuthMethod = 'google' | 'email'

const getFirebaseAuthErrorCode = (error: unknown): string => {
    if (error && typeof error === 'object' && 'code' in error) {
        return String((error as AuthError).code)
    }
    return ''
}

const getUserDoc = async (
    uid: string,
    displayName: string,
    email: string,
    photoURL: string,
    phoneNumber: string,
    authMethod: AuthMethod = 'google',
    attrs?: JSON
) => {
    try {
        if (!uid) {
            throw Error('Missing uid')
        }

        const db = getFirestoreInstance()
        const docRef = doc(db, 'users', uid)
        const docSnap = await getDoc(docRef)
        const signinEvent = authMethod === 'google' ? 'googleSignin' : 'emailSignin'
        const signupEvent = authMethod === 'google' ? 'googleSignup' : 'emailSignup'

        if (docSnap.exists()) {
            sendGAEvent('event', signinEvent, { uid })
            await setDoc(docRef, {
                last_login: Timestamp.fromDate(new Date()),
                display_name: displayName,
                photo_url: photoURL,
                phone_number: phoneNumber,
                ...attrs,
            }, { merge: true })

            const updatedSnap = await getDoc(docRef)
            return updatedSnap.data()
        }

        sendGAEvent('event', signupEvent, { uid })
        await setDoc(docRef, {
            created_time: Timestamp.fromDate(new Date()),
            last_login: Timestamp.fromDate(new Date()),
            email,
            display_name: displayName,
            photo_url: photoURL,
            phone_number: phoneNumber,
            uid,
            alpha_tester: true,
            user_type: {
                type: 'normal',
                updated_time: Timestamp.fromDate(new Date()),
            },
            crawl_limit: { ...DEFAULT_USER_CRAWL_LIMIT },
            ...attrs,
        }, { merge: true })
        const createdSnap = await getDoc(docRef)
        return createdSnap.data()
    } catch (err) {
        logger.error(err instanceof Error ? err : new Error(String(err)), 'getUserDoc error', [], undefined)
        throw err
    }
}

const LOCAL_STORAGE_KEY_USER_CONTEXT = 'UserContext'

const setUserSessContext = async (login_type: string, doc_data: DocumentData) => {
    const { refreshToken, accessToken, idToken, uid, display_name, user_type } = doc_data

    const sess_payload = {
        refreshToken,
        accessToken,
        idToken,
        userName: display_name,
        userType: user_type,
        uid,
    }

    sessionStorage.setItem(LOCAL_STORAGE_KEY_USER_CONTEXT, JSON.stringify(sess_payload))
    return true
}

const getUserSessionContext = () => {
    const user_context = sessionStorage.getItem(LOCAL_STORAGE_KEY_USER_CONTEXT)
    return user_context ? JSON.parse(user_context) : null
}

const persistAuthUser = async (user: User, authMethod: AuthMethod, tokens?: {
    accessToken?: string | null
    idToken?: string | null
}) => {
    const { displayName, email, uid, photoURL, phoneNumber } = user
    const doc_data = await getUserDoc(
        uid,
        displayName ?? '',
        email ?? '',
        photoURL ?? '',
        phoneNumber ?? '',
        authMethod
    )
    if (doc_data) {
        await setUserSessContext(
            authMethod === 'google' ? 'Google Signin' : 'Email Signin',
            {
                accessToken: tokens?.accessToken ?? '',
                idToken: tokens?.idToken ?? '',
                ...doc_data,
            }
        )
    }
    return doc_data
}


const googleSignIn = async () => {
    const provider = new GoogleAuthProvider();

    // Request offline access
    provider.setCustomParameters({
      access_type: 'offline',
      prompt: 'consent',
    });

    try {
        sessionStorage.removeItem('not_google_signin')
        const auth = getAuth();

        if (auth.currentUser && !auth.currentUser.isAnonymous) {
          return {
            user: auth.currentUser,
            credential: null,
            accessToken: null,
          };
        }

        const result = await signInWithPopup(auth, provider);
        const credential = GoogleAuthProvider.credentialFromResult(result);

        if (!credential) {
          throw new Error('No credential returned from Google');
        }

        try {
          await persistAuthUser(result.user, 'google', {
            accessToken: credential.accessToken,
            idToken: credential.idToken,
          })
        } catch (err) {
          logger.error(err instanceof Error ? err : new Error(String(err)), 'Google Sign-In Error getUserDoc', [], undefined)
          throw err
        }

      return {
        user: result.user,
        credential,
        accessToken: credential.accessToken,
      };
    } catch (error) {
      logger.error(error instanceof Error ? error : new Error(String(error)), 'Google Sign-In Error', [], undefined);
      throw error;
    }
};

const MISSING_ACCOUNT_ERROR_CODES = new Set([
  'auth/user-not-found',
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
])

const emailPasswordSignIn = async (email: string, password: string) => {
  const trimmedEmail = email.trim()
  if (!trimmedEmail || !password) {
    throw new Error('Email and password are required')
  }
  if (password.length < 6) {
    throw new Error('Password must be at least 6 characters')
  }

  const auth = getAuth()

  try {
    sessionStorage.removeItem('not_google_signin')

    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      return { user: auth.currentUser }
    }

    let userCredential
    try {
      userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, password)
    } catch (signInError) {
      const code = getFirebaseAuthErrorCode(signInError)
      if (!MISSING_ACCOUNT_ERROR_CODES.has(code)) {
        throw signInError
      }

      try {
        userCredential = await createUserWithEmailAndPassword(auth, trimmedEmail, password)
      } catch (createError) {
        const createCode = getFirebaseAuthErrorCode(createError)
        // Account already exists (or email enumeration protection): treat as bad credentials.
        if (createCode === 'auth/email-already-in-use') {
          throw new Error('Invalid email or password')
        }
        throw createError
      }
    }

    await persistAuthUser(userCredential.user, 'email')
    return { user: userCredential.user }
  } catch (error) {
    logger.error(
      error instanceof Error ? error : new Error(String(error)),
      'Email/Password Sign-In Error',
      [],
      undefined
    )
    throw error
  }
};

const getEmailPasswordErrorMessage = (error: unknown): string => {
  if (error instanceof Error && !('code' in error)) {
    return error.message
  }

  switch (getFirebaseAuthErrorCode(error)) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address'
    case 'auth/weak-password':
      return 'Password must be at least 6 characters'
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
      return 'Invalid email or password'
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later'
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again'
    default:
      return error instanceof Error ? error.message : 'Unable to sign in. Please try again'
  }
}

const handleTokenExpiredError = async () => {
  const auth = getAuth()
  await signOut(auth)
  // Note: We don't handle navigation here since it should be handled by the component
  sessionStorage.removeItem(LOCAL_STORAGE_KEY_USER_CONTEXT)
}

export {
  googleSignIn,
  emailPasswordSignIn,
  getEmailPasswordErrorMessage,
  getUserSessionContext,
  LOCAL_STORAGE_KEY_USER_CONTEXT,
  handleTokenExpiredError,
};
